import {
  type BillingCommandAffectedResource,
  toBillingCommandErrorMessage,
  type WebsocketBillingCommandUpdate,
} from "@intelligems/ig-types";
import type { QueryClient } from "@tanstack/react-query";

import {
  adminKeys,
  billingModulesKeys,
  intelligemsKeys,
  organizationsKeys,
  subscriptionProductKeys,
} from "@src/api/queryKeys";

// failed_retryable is deliberately absent: the command is not settled — it
// restarts on the same row — and toasting an app-wide error for a state the
// workflow will recover from on its own is noise.
const BILLING_COMMAND_ERROR_STATUSES = new Set<string>([
  "rejected",
  "failed_exhausted",
  "abandoned_external_approval",
]);

const LEGACY_BILLING_COMMAND_AFFECTED_RESOURCES: readonly BillingCommandAffectedResource[] =
  [
    "billingWorkflowTrace",
    "organizationSummary",
    "organizationSummaries",
    "stripeSubscriptionLinks",
    "subscriptionProducts",
    "subscriptionAccess",
    "agencyBilling",
    "stripeApi",
  ];

const getBillingCommandErrorMessage = (
  message: WebsocketBillingCommandUpdate
) => {
  if (!message.status || !BILLING_COMMAND_ERROR_STATUSES.has(message.status)) {
    return undefined;
  }

  // Prefer the merchant-safe catalog copy for the code: raw errorMessage can
  // be a Shopify/Stripe API string that was never written for merchants.
  return (
    toBillingCommandErrorMessage(message.errorCode) ??
    message.errorMessage ??
    "Billing command failed"
  );
};

const invalidateBillingWorkflowTraceQueries = ({
  message,
  queryClient,
}: {
  message: WebsocketBillingCommandUpdate;
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    queryKey: ["billingWorkflowTrace", "recent"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["billingWorkflowTrace", "search"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["billingWorkflowTrace", "overview"],
  });
  void queryClient.invalidateQueries({
    queryKey: ["billingWorkflowTrace", "command", message.billingCommandId],
  });
};

const getAffectedResources = (message: WebsocketBillingCommandUpdate) =>
  new Set(
    message.affectedResources?.length
      ? message.affectedResources
      : LEGACY_BILLING_COMMAND_AFFECTED_RESOURCES
  );

const invalidateOrganizationSummaryQueries = ({
  orgId,
  queryClient,
}: {
  orgId: string;
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.orgSummary(orgId),
  });
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.one,
  });
};

const invalidateOrganizationSummariesQueries = ({
  queryClient,
}: {
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [key] = query.queryKey;
      return (
        typeof key === "object" &&
        key !== null &&
        "scope" in key &&
        key.scope === "organizations" &&
        "orgSummaries" in key &&
        key.orgSummaries === true
      );
    },
  });
};

const invalidateStripeSubscriptionLinksQueries = ({
  orgId,
  queryClient,
}: {
  orgId: string;
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.stripeSubscriptionLinks,
  });
  void queryClient.invalidateQueries({
    queryKey: adminKeys.stripeSubscriptionLinks(orgId),
  });
};

const invalidateSubscriptionProductQueries = ({
  orgId,
  queryClient,
}: {
  orgId: string;
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    queryKey: subscriptionProductKeys.one(orgId),
  });
  void queryClient.invalidateQueries({
    queryKey: subscriptionProductKeys.all,
  });
};

const invalidateAgencyBillingQueries = ({
  queryClient,
}: {
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [key] = query.queryKey;
      return (
        typeof key === "object" &&
        key !== null &&
        "scope" in key &&
        key.scope === "admin" &&
        (("agencies" in key && key.agencies === true) ||
          ("getPendingAgencyDowngrades" in key &&
            key.getPendingAgencyDowngrades === true) ||
          ("getPendingAgencyCancellations" in key &&
            key.getPendingAgencyCancellations === true) ||
          ("organizationAgencyLink" in key &&
            key.organizationAgencyLink === true) ||
          ("previewBrandPlanOverages" in key &&
            key.previewBrandPlanOverages === true))
      );
    },
  });
};

const invalidateStripeApiQueries = ({
  queryClient,
}: {
  queryClient: QueryClient;
}) => {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const [key] = query.queryKey;
      return (
        typeof key === "object" &&
        key !== null &&
        "scope" in key &&
        key.scope === "stripeApi"
      );
    },
  });
};

export const handleAdminBillingCommandUpdate = ({
  message,
  queryClient,
}: {
  message: WebsocketBillingCommandUpdate;
  queryClient: QueryClient;
}) => {
  invalidateBillingWorkflowTraceQueries({ message, queryClient });
};

export const handleBillingCommandUpdate = ({
  message,
  notifyError,
  orgId,
  queryClient,
}: {
  message: WebsocketBillingCommandUpdate;
  orgId: string;
  queryClient: QueryClient;
  notifyError?: (message: string) => void;
}) => {
  if (message.orgId !== orgId) {
    return;
  }

  const errorMessage = getBillingCommandErrorMessage(message);
  if (errorMessage) {
    notifyError?.(errorMessage);
  }

  const affectedResources = getAffectedResources(message);

  if (affectedResources.has("billingWorkflowTrace")) {
    invalidateBillingWorkflowTraceQueries({ message, queryClient });
  }
  if (affectedResources.has("organizationSummary")) {
    invalidateOrganizationSummaryQueries({ orgId, queryClient });
  }
  if (affectedResources.has("organizationSummaries")) {
    invalidateOrganizationSummariesQueries({ queryClient });
  }
  if (affectedResources.has("stripeSubscriptionLinks")) {
    invalidateStripeSubscriptionLinksQueries({ orgId, queryClient });
  }
  if (affectedResources.has("subscriptionProducts")) {
    invalidateSubscriptionProductQueries({ orgId, queryClient });
  }
  if (affectedResources.has("subscriptionAccess")) {
    void queryClient.invalidateQueries({
      queryKey: billingModulesKeys.subscriptionAccess(orgId),
    });
    // The live catalog is the other half of the same read: module ownership is
    // resolved by matching the subscription's price IDs against it, and its
    // per-entry price IDs follow the org's order-count tier. Refreshing access
    // alone leaves the two halves describing different plans.
    void queryClient.invalidateQueries({
      queryKey: intelligemsKeys.livePrices,
    });
  }
  if (affectedResources.has("agencyBilling")) {
    invalidateAgencyBillingQueries({ queryClient });
  }
  if (affectedResources.has("stripeApi")) {
    invalidateStripeApiQueries({ queryClient });
  }
};
